import { create } from 'zustand';

import type { Appearance } from '@/types';

/**
 * Light / dark / system appearance.
 *
 * Kept separate from the auth store because it applies to signed-out surfaces
 * too — the login screen, the kiosk, the public complaint status page — and
 * because it must be readable synchronously before React mounts.
 */

const STORAGE_KEY = 'dbcx.appearance';

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

function stored(): Appearance {
    try {
        const value = localStorage.getItem(STORAGE_KEY);

        if (value === 'light' || value === 'dark' || value === 'system') {
            return value;
        }
    } catch {
        // Storage unavailable — fall through to the system preference.
    }

    return 'system';
}

function apply(appearance: Appearance): void {
    const dark =
        appearance === 'dark' || (appearance === 'system' && media().matches);

    document.documentElement.classList.toggle('dark', dark);

    // Tells the browser to render form controls, scrollbars and the address bar
    // to match. Without it, native controls stay light inside a dark interface.
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
}

interface AppearanceState {
    appearance: Appearance;
    setAppearance: (appearance: Appearance) => void;
}

export const useAppearanceStore = create<AppearanceState>((set) => ({
    appearance: 'system',

    setAppearance: (appearance) => {
        apply(appearance);

        try {
            localStorage.setItem(STORAGE_KEY, appearance);
        } catch {
            // Persistence is a nicety; the change still applies for this session.
        }

        set({ appearance });
    },
}));

/**
 * Apply the stored appearance and follow the OS preference while set to
 * `system`.
 *
 * The Blade shell already applies the correct class before first paint to avoid a
 * flash; this takes over afterwards and keeps it in step if the user changes
 * their OS theme with the app open.
 */
export function initialiseAppearance(): void {
    const initial = stored();

    apply(initial);

    useAppearanceStore.setState({ appearance: initial });

    media().addEventListener('change', () => {
        if (useAppearanceStore.getState().appearance === 'system') {
            apply('system');
        }
    });
}
